import React from 'react'
import { MultiLineChart } from './MultiLineChart'
import { DonutChart } from './DonutChart'
import { Card, COLOR_MAP } from './Common'
import { formatBs } from './utils'

const EMPRESA_COLORS = ['blue', 'purple', 'green', 'pink', 'cyan', 'orange']

// Tarjeta liviana (sin ícono grande) pensada para mostrar varios números juntos sin verse
// apretada — el acento de color va en un borde izquierdo fino, no en cada tarjeta entera.
const MiniStat = ({ label, value, color = 'yellow' }) => {
    const c = COLOR_MAP[color] ?? COLOR_MAP.yellow
    return (
        <div className={`bg-white dark:bg-gray-800/60 border-l-4 ${c.accent} border-y border-r border-gray-200 dark:border-gray-700 rounded-r-lg px-4 py-3 min-w-0`}>
            <div className='text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide truncate'>{label}</div>
            <div className='text-lg font-bold text-gray-800 dark:text-gray-100 truncate'>{value}</div>
        </div>
    )
}

// Panel de una empresa (o el combinado): nombre + grilla de mini-stats + gráfico opcional,
// todo dentro de una sola tarjeta con acento de color para que cada empresa se distinga de un vistazo.
const Panel = ({ nombre, data, color, children }) => {
    const c = COLOR_MAP[color] ?? COLOR_MAP.yellow
    return (
        <div className={`bg-gray-50 dark:bg-gray-900/40 border-l-[6px] ${c.accent} border-y border-r border-gray-200 dark:border-gray-700 rounded-r-xl p-5 min-w-0 grid gap-4`}>
            <div className='flex items-center gap-2 text-lg font-bold text-gray-800 dark:text-gray-100'>
                <i className='fa-solid fa-building' />
                {nombre}
            </div>

            <div className='grid grid-cols-2 sm:grid-cols-4 gap-3 min-w-0'>
                <MiniStat label='Ventas' value={`Bs ${formatBs(data.ventaTotal)}`} color={color} />
                <MiniStat label='Vtas. clientes' value={`Bs ${formatBs(data.totalVentasClientes)}`} color={color} />
                <MiniStat label='Vtas. emp/mayor.' value={`Bs ${formatBs(data.totalVentasEmpresasYMayoristas)}`} color={color} />
                <MiniStat label='Gastos' value={`Bs ${formatBs(data.totalGastos)}`} color={color} />
                <MiniStat label='Deudas' value={`Bs ${formatBs(data.deudaTotal)}`} color={color} />
                <MiniStat label='Garantías' value={`Bs ${formatBs(data.garantiaTotal)}`} color={color} />
                <MiniStat label='Envases prestados' value={`${data.envasesPrestadosTotal ?? 0}`} color={color} />
                <MiniStat label='Envases en garantía' value={`${data.envasesGarantiaTotal ?? 0}`} color={color} />
            </div>

            {children && <Card>{children}</Card>}
        </div>
    )
}

export const ResumenGeneral = ({ resumen }) => {
    const { empresas = [], general = {} } = resumen ?? {}
    const empresasFalladas = empresas.filter((e) => !e.ok)
    const empresasOk = empresas.filter((e) => e.ok)
    const hayVariasEmpresas = empresas.length > 1

    // Arma el {granularidad, vendedores:[{nombre, serie}]} que espera MultiLineChart, tratando
    // cada empresa como si fuera un "vendedor" más — así se reutiliza el mismo componente para
    // comparar entre empresas en vez de entre vendedores dentro de una empresa.
    const comparativoPorEmpresa = (campoSerie) => ({
        granularidad: empresasOk[0]?.[campoSerie]?.granularidad || 'day',
        vendedores: empresasOk.map((e) => ({
            user_id: e.nombre,
            nombre: e.nombre,
            codigo: null,
            serie: e[campoSerie]?.serie ?? [],
        })),
    })

    const comparativoVentas = comparativoPorEmpresa('serieVentaTotal')
    const comparativoGastos = comparativoPorEmpresa('serieGastosTotal')
    const comparativoGarantias = comparativoPorEmpresa('serieGarantiaTotal')
    const comparativoPrestamos = comparativoPorEmpresa('seriePrestamoTotal')
    const comparativoDeudas = comparativoPorEmpresa('serieDeudaTotal')

    return (
        <div className='grid gap-6 min-w-0'>
            {empresasFalladas.length > 0 && (
                <div className='bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300 px-4 py-2 rounded-md text-sm'>
                    <i className='fa fa-triangle-exclamation mr-1' />
                    No se pudo obtener datos de: {empresasFalladas.map((e) => e.nombre).join(', ')}.
                </div>
            )}

            {hayVariasEmpresas && (
                <Panel nombre='TOTAL COMBINADO' data={general} color='slate'>
                    <div className='grid grid-cols-1 lg:grid-cols-2 gap-8 min-w-0'>
                        <MultiLineChart title='COMPARATIVO DE VENTAS POR EMPRESA' serieData={comparativoVentas} />
                        <MultiLineChart title='COMPARATIVO DE GASTOS POR EMPRESA' serieData={comparativoGastos} />
                        <MultiLineChart title='COMPARATIVO DE DEUDAS POR EMPRESA' serieData={comparativoDeudas} />
                        <MultiLineChart title='COMPARATIVO DE GARANTÍAS POR EMPRESA' serieData={comparativoGarantias} />
                        <MultiLineChart title='COMPARATIVO DE PRÉSTAMOS DE ENVASES POR EMPRESA' serieData={comparativoPrestamos} />
                    </div>
                </Panel>
            )}

            {empresasOk.map((empresa, idx) => {
                const color = EMPRESA_COLORS[idx % EMPRESA_COLORS.length]
                return (
                    <Panel key={empresa.nombre} nombre={empresa.nombre} data={empresa} color={color}>
                        {hayVariasEmpresas && (
                            <DonutChart
                                title='VENTAS VS DEUDAS'
                                labels={['Ventas', 'Deudas']}
                                data={[empresa.ventaTotal ?? 0, empresa.deudaTotal ?? 0]}
                            />
                        )}
                    </Panel>
                )
            })}
        </div>
    )
}
